import { useCallback, useEffect, useRef, useState } from 'react';
import tradingService from '../services/tradingService';

export default function useAccountKeys(number) {
  // modal state
  const [keysViewOpen, setKeysViewOpen] = useState(false);
  const [keysEditOpen, setKeysEditOpen] = useState(false);

  // loading/error state
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysSaving, setKeysSaving] = useState(false);
  const [keysError, setKeysError] = useState('');
  const [keysSaveError, setKeysSaveError] = useState('');
  const [keysSaveSuccess, setKeysSaveSuccess] = useState('');

  // server snapshot (what we display in "view")
  const [keys, setKeys] = useState({ public_key: '', secret_key: '', trading_password: '' });

  // draft fields (what we edit in "update")
  const [keysApiKey, setKeysApiKey] = useState('');
  const [keysApiSecret, setKeysApiSecret] = useState('');
  const [keysApiPassphrase, setKeysApiPassphrase] = useState('');

  const abortRef = useRef(null);

  const abortInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => abortInFlight();
  }, [abortInFlight]);

  const loadKeys = useCallback(async () => {
    if (!number) return;

    abortInFlight();
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      setKeysLoading(true);
      setKeysError('');

      const res = await tradingService.accounts.keys.get({ number, signal: ac.signal });
      const data = res?.data ?? {};

      const nextKeys = {
        public_key: data.public_key ?? '',
        secret_key: data.secret_key ?? '',
        trading_password: data.trading_password ?? '',
      };

      setKeys(nextKeys);

      // preload edit fields too (user can overwrite)
      setKeysApiKey(nextKeys.public_key);
      setKeysApiSecret(nextKeys.secret_key);
      setKeysApiPassphrase(nextKeys.trading_password);
    } catch (err) {
      // ignore abort noise, but keep it simple
      if (err?.name !== 'AbortError') {
        setKeysError(err?.message || 'Failed to load keys');
        setKeys({ public_key: '', secret_key: '', trading_password: '' });
      }
    } finally {
      setKeysLoading(false);
    }
  }, [number, abortInFlight]);

  const openViewKeys = useCallback(async () => {
    setKeysError('');
    setKeysSaveError('');
    setKeysSaveSuccess('');
    setKeysViewOpen(true);
    await loadKeys();
  }, [loadKeys]);

  const openEditKeys = useCallback(async () => {
    setKeysSaveError('');
    setKeysSaveSuccess('');
    setKeysEditOpen(true);
    await loadKeys();
  }, [loadKeys]);

  const onSaveKeys = useCallback(
    async (e) => {
      e.preventDefault();
      if (!number) return;

      setKeysSaveError('');
      setKeysSaveSuccess('');
      setKeysSaving(true);

      try {
        const res = await tradingService.accounts.keys.set({
          number,
          api_key: keysApiKey,
          api_secret: keysApiSecret,
          api_passphrase: keysApiPassphrase,
        });

        const data = res?.data ?? {};
        setKeys({
          public_key: data.public_key ?? keysApiKey ?? '',
          secret_key: data.secret_key ?? keysApiSecret ?? '',
          trading_password: data.trading_password ?? keysApiPassphrase ?? '',
        });

        setKeysSaveSuccess('Keys updated.');
      } catch (err) {
        setKeysSaveError(err?.message || 'Failed to update keys');
      } finally {
        setKeysSaving(false);
      }
    },
    [number, keysApiKey, keysApiSecret, keysApiPassphrase]
  );

  const keysDisabled = keysLoading || keysSaving;

  return {
    // modals
    keysViewOpen,
    setKeysViewOpen,
    keysEditOpen,
    setKeysEditOpen,
    openViewKeys,
    openEditKeys,

    // data
    keys,
    loadKeys,

    // draft fields
    keysApiKey,
    setKeysApiKey,
    keysApiSecret,
    setKeysApiSecret,
    keysApiPassphrase,
    setKeysApiPassphrase,

    // status
    keysLoading,
    keysSaving,
    keysDisabled,
    keysError,
    keysSaveError,
    keysSaveSuccess,

    // actions
    onSaveKeys,
    setKeysSaveSuccess,
    setKeysSaveError,
    setKeysError,
  };
}